import { useRef } from 'react';
import { useJobStatus } from './useLeadImportApi';
import { isPollingEnabled } from '../utils/progressHelpers';
import { MAX_POLL_ATTEMPTS } from '../utils/constants';
import type { JobStatusResponse } from '../utils/types';

export { isPollingEnabled } from '../utils/progressHelpers';

interface UseJobPollingReturn {
  jobStatus: JobStatusResponse | null;
  isPolling: boolean;
  pollCount: number;
  error: Error | null;
}

/**
 * Hook that polls job status at a fixed interval, auto-stopping when the job
 * reaches a terminal state or the maximum poll attempts are exceeded.
 *
 * Uses React Query's refetchInterval under the hood via useJobStatus.
 * Tracks poll count with a ref that increments on each successful data fetch.
 * Stops polling when:
 *   - jobID is null
 *   - status is terminal (not queued/processing)
 *   - pollCount >= MAX_POLL_ATTEMPTS (300)
 */
export function useJobPolling(jobID: string | null): UseJobPollingReturn {
  const pollCountRef = useRef(0);
  const currentStatus = useRef<string | null>(null);
  const lastDataUpdatedAt = useRef(0);

  // Determine if polling should be enabled based on current status and poll count
  const shouldPoll =
    !!jobID &&
    pollCountRef.current < MAX_POLL_ATTEMPTS &&
    (currentStatus.current === null || isPollingEnabled(currentStatus.current));

  const query = useJobStatus(jobID, shouldPoll);

  // Track poll count by observing dataUpdatedAt changes
  if (query.dataUpdatedAt > lastDataUpdatedAt.current) {
    lastDataUpdatedAt.current = query.dataUpdatedAt;
    if (query.data) {
      pollCountRef.current += 1;
      currentStatus.current = query.data.status;
    }
  }

  // Determine if we've hit the timeout
  const hasTimedOut =
    pollCountRef.current >= MAX_POLL_ATTEMPTS &&
    !!currentStatus.current &&
    isPollingEnabled(currentStatus.current);

  const timeoutError = hasTimedOut
    ? new Error(
        'Import is taking longer than expected. Please refresh the page to check the current status.'
      )
    : null;

  const isPolling =
    !!jobID &&
    pollCountRef.current < MAX_POLL_ATTEMPTS &&
    !!currentStatus.current &&
    isPollingEnabled(currentStatus.current);

  return {
    jobStatus: query.data ?? null,
    isPolling,
    pollCount: pollCountRef.current,
    error: timeoutError ?? query.error ?? null,
  };
}
